import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { useLocation, useNavigate } from 'react-router-dom';
import './Profile.css';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  strava_connected: boolean;
  nationality?: string;
  weight?: number;
  gender?: string;
  bike_type?: string;
  height_cm?: number;
  body_fat?: number;
  date_of_birth?: string;
  google_connected?: boolean;
  google_picture?: string;
  health_connected?: boolean;
  health_last_sync?: string;
  whoop_connected?: boolean;
  intervals_connected?: boolean;
}

interface ProfileProps {
  user: User;
  onStravaAuth: () => void;
}

const Profile: React.FC<ProfileProps> = ({ user, onStravaAuth }) => {
  console.log('Profile user prop:', user);
  const [stravaProfile, setStravaProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    nationality: user.nationality || '',
    weight: user.weight !== undefined && user.weight !== null ? String(user.weight) : '',
    gender: user.gender || '',
    bike_type: user.bike_type || '',
    height_cm: user.height_cm !== undefined && user.height_cm !== null ? String(user.height_cm) : '',
    body_fat: user.body_fat !== undefined && user.body_fat !== null ? String(user.body_fat) : '',
    date_of_birth: user.date_of_birth || ''
  });
  const [saving, setSaving] = useState(false);
  const { t, language: currentLanguage, setLanguage, availableLanguages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [stravaMessage, setStravaMessage] = useState<string | null>(null);
  const [whoopMessage, setWhoopMessage] = useState<string | null>(null);
  const [whoopSyncing, setWhoopSyncing] = useState(false);
  const [whoopSyncResult, setWhoopSyncResult] = useState<string | null>(null);
  const [ftpHistory, setFtpHistory] = useState<{date: string; ftp: number}[]>([]);
  const [ftpDate, setFtpDate] = useState(new Date().toISOString().split('T')[0]);
  const [ftpValue, setFtpValue] = useState('');
  const [ftpSaving, setFtpSaving] = useState(false);

  // Intervals.icu
  const [intervalsConnected, setIntervalsConnected] = useState(false);
  const [intervalsMessage, setIntervalsMessage] = useState<string | null>(null);
  const [intervalsError, setIntervalsError] = useState(false);

  const loadFtpHistory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/analytics/ftp`, { credentials: 'include' });
      if (res.ok) setFtpHistory(await res.json());
    } catch {}
  };

  const addFtpEntry = async () => {
    const ftp = parseInt(ftpValue);
    if (!ftp || ftp < 50 || ftp > 600) return;
    setFtpSaving(true);
    try {
      await fetch(`${API_BASE_URL}/api/analytics/ftp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: ftpDate, ftp })
      });
      setFtpValue('');
      await loadFtpHistory();
    } catch {} finally { setFtpSaving(false); }
  };

  const currentFtp = ftpHistory.length > 0
    ? ftpHistory.reduce((latest, entry) => entry.date > latest.date ? entry : latest).ftp
    : null;

  const deleteFtpEntry = async (date: string) => {
    try {
      await fetch(`${API_BASE_URL}/api/analytics/ftp?date=${date}`, {
        method: 'DELETE', credentials: 'include'
      });
      await loadFtpHistory();
    } catch {}
  };

  useEffect(() => { loadFtpHistory(); }, []);

  // Load Intervals.icu status
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/web/intervals/status`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setIntervalsConnected(data.connected);
        }
      } catch {}
    })();
  }, []);

  const handleIntervalsDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/web/intervals/disconnect`, { method: 'POST', credentials: 'include' });
      setIntervalsConnected(false);
      setIntervalsMessage('Disconnected from Intervals.icu');
      setIntervalsError(false);
    } catch {}
  };

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleAuthCallback(code);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const strava = urlParams.get('strava');
    if (strava === 'success') setStravaMessage('Połączono ze Strava!');
    if (strava === 'disconnected') setStravaMessage('Rozłączono ze Strava!');
    if (strava === 'error') setStravaMessage('Błąd połączenia ze Strava!');
    const whoop = urlParams.get('whoop');
    if (whoop === 'connected') setWhoopMessage('WHOOP connected successfully!');
    if (whoop === 'error') setWhoopMessage('Failed to connect WHOOP. Please try again.');
    const intervalsConnectedParam = urlParams.get('intervals_connected');
    const intervalsErrorParam = urlParams.get('intervals_error');
    if (intervalsConnectedParam === '1') {
      setIntervalsConnected(true);
      setIntervalsMessage('Connected to Intervals.icu!');
      setIntervalsError(false);
    }
    if (intervalsErrorParam) {
      setIntervalsMessage(decodeURIComponent(intervalsErrorParam));
      setIntervalsError(true);
    }
    if (strava || whoop || intervalsConnectedParam || intervalsErrorParam) {
      // Clean up URL
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    // Logujemy usera dla debugowania
    console.log('Profile useEffect user:', user);
    setStravaMessage(null);
    setProfileData({
      nationality: user.nationality || '',
      weight: user.weight !== undefined && user.weight !== null ? String(user.weight) : '',
      gender: user.gender || '',
      bike_type: user.bike_type || '',
      height_cm: user.height_cm !== undefined && user.height_cm !== null ? String(user.height_cm) : '',
      body_fat: user.body_fat !== undefined && user.body_fat !== null ? String(user.body_fat) : '',
      date_of_birth: user.date_of_birth || ''
    });
  }, [user]);

  const handleAuthCallback = async (code: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/callback?code=${code}`, {
        credentials: 'include'
      });
      if (response.ok) {
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
      }
    } catch (error) {
      console.error('Auth callback failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        alert(t('profile.profileUpdated'));
        window.location.reload();
      } else {
        alert('Error updating profile');
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      alert('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleStravaDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/strava-disconnect`, {
        method: 'POST',
        credentials: 'include',
      });
      setStravaMessage('Rozłączono ze Strava!');
      window.location.reload();
    } catch (error) {
      setStravaMessage('Błąd rozłączania ze Strava!');
    }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/google-disconnect`, {
        method: 'POST',
        credentials: 'include',
      });
      window.location.reload();
    } catch (error) {
      console.error('Google disconnect failed:', error);
    }
  };

  const handleWhoopConnect = () => {
    window.location.href = `${API_BASE_URL}/auth/whoop/connect`;
  };

  const handleWhoopDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/web/whoop/disconnect`, {
        method: 'POST',
        credentials: 'include',
      });
      window.location.reload();
    } catch (e) {
      console.error('Failed to disconnect WHOOP:', e);
    }
  };

  const handleWhoopSync = async () => {
    setWhoopSyncing(true);
    setWhoopSyncResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/whoop/sync`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setWhoopSyncResult('Data synced successfully!');
      } else {
        setWhoopSyncResult('Sync failed. Please try again.');
      }
    } catch (e) {
      console.error('WHOOP sync failed:', e);
      setWhoopSyncResult('Sync failed. Please try again.');
    } finally {
      setWhoopSyncing(false);
    }
  };

  // Dodaję stan do obsługi zmiany hasła
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Add state for account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    setPasswordError(null);
    setChangingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
          repeat_password: repeatPassword
        })
      });
      const data = await response.json();
      if (response.ok) {
        setPasswordMsg(data.message || t('profile.passwordChanged'));
        setOldPassword('');
        setNewPassword('');
        setRepeatPassword('');
      } else {
        setPasswordError(data.error || 'Błąd zmiany hasła');
      }
    } catch (error) {
      setPasswordError('Błąd połączenia z serwerem');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError(null);
    
    if (user.google_connected) {
      if (!deleteEmail.trim()) {
        setDeleteError(t('profile.enterEmailToConfirm'));
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(deleteEmail.trim())) {
        setDeleteError(t('profile.invalidEmail'));
        return;
      }
    } else {
    if (!deletePassword.trim()) {
        setDeleteError(t('profile.enterPasswordToConfirm'));
      return;
      }
    }
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/delete-account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          password: user.google_connected ? undefined : deletePassword,
          email: user.google_connected ? deleteEmail.trim() : undefined
        })
      });
      
      if (response.ok) {
        alert('Your account has been deleted successfully.');
        // Redirect to logout/landing page
        window.location.href = '/';
      } else {
        const error = await response.json();
        setDeleteError(error.error || 'Failed to delete account');
      }
    } catch (error) {
      setDeleteError('Network error occurred');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setDeleteError(null);
    setDeletePassword('');
    setDeleteEmail('');
  };

  return (
    <div className="card profile-grid-main">
      <h2>{t('profile.title')}</h2>
      {/* Top row: User Info (left), Google + Strava (right) */}
      <div className="profile-top-row">
        <div className="profile-user-info">
          <h3>{t('profile.userInfo.title')}</h3>
          <table className="user-info-table">
            <tbody>
              <tr>
                <td>{t('profile.userInfo.username')}:</td>
                <td>{user.username}</td>
              </tr>
              <tr>
                <td>{t('profile.userInfo.firstName')}:</td>
                <td>{user.first_name}</td>
              </tr>
              <tr>
                <td>{t('profile.userInfo.lastName')}:</td>
                <td>{user.last_name}</td>
              </tr>
              <tr>
                <td>{t('profile.userInfo.role')}:</td>
                <td>{user.is_admin ? t('profile.userInfo.admin') : t('profile.userInfo.user')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="profile-oauth-column">
          <div className="profile-google-section">
            <h4>Google Account</h4>
            <div className="oauth-actions-container">
              <div className="oauth-button-group">
              {!user.google_connected ? (
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.location.href = `${API_BASE_URL}/auth/google`}
                  style={{ backgroundColor: '#4285f4', borderColor: '#4285f4' }}
                >
                  {t('profile.connectGoogle')}
                </button>
              ) : (
                  <button
                    onClick={handleGoogleDisconnect}
                    className="btn btn-danger"
                  >
                    {t('profile.disconnectGoogle')}
                  </button>
              )}
                <div className={`status-indicator ${user.google_connected ? 'connected' : 'disconnected'}`}>
                  {user.google_connected ? '🔗 Connected' : '❌ Disconnected'}
                </div>
              </div>
            </div>
          </div>
          <div className="profile-strava-section">
            <h4>Strava Account</h4>
            <div className="oauth-actions-container">
              <div className="oauth-button-group">
              {!user.strava_connected ? (
                <button className="btn btn-primary" onClick={onStravaAuth}>
                  {t('profile.userInfo.connect')}
                </button>
              ) : (
                  <button
                    onClick={handleStravaDisconnect}
                    className="btn btn-danger"
                  >
                    {t('strava.disconnect')}
                  </button>
              )}
                <div className={`status-indicator ${user.strava_connected ? 'connected' : 'disconnected'}`}>
                  {user.strava_connected ? '🔗 Connected' : '❌ Disconnected'}
                </div>
              </div>
            </div>
          </div>
          {user.health_connected && (
            <div className="profile-health-section">
              <h4>Health Data</h4>
              <div className="oauth-actions-container">
                <div className="oauth-button-group">
                  <div className={`status-indicator connected`}>
                    🔗 Connected
                  </div>
                  {user.health_last_sync && (
                    <span className="health-last-sync">
                      {t('profile.lastSync')}: {new Date(user.health_last_sync).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <p className="health-info-text">{t('profile.healthSyncInfo')}</p>
            </div>
          )}
          <div className="profile-whoop-section">
            <h4>WHOOP</h4>
            <p className="whoop-desc">
              Connect your WHOOP band to automatically sync recovery, sleep, and strain data with your AI coach.
            </p>
            {whoopMessage && (
              <div className={`whoop-message ${whoopMessage.includes('Failed') ? 'error' : 'success'}`}>
                {whoopMessage}
                <button className="whoop-message-close" onClick={() => setWhoopMessage(null)}>&times;</button>
              </div>
            )}
            <div className="oauth-actions-container">
              <div className="oauth-button-group">
                {user.whoop_connected ? (
                  <>
                    <div className="status-indicator connected">🔗 Connected</div>
                    <button className="btn btn-secondary" onClick={handleWhoopSync} disabled={whoopSyncing}>
                      {whoopSyncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                    <button className="btn btn-danger" onClick={handleWhoopDisconnect} disabled={whoopSyncing} style={{ fontSize: '0.8rem', padding: '7px 14px' }}>
                      Disconnect
                    </button>
                  </>
                ) : (
                  <>
                    <div className="status-indicator disconnected">❌ Not connected</div>
                    <button className="btn btn-primary" onClick={handleWhoopConnect}>
                      Connect WHOOP
                    </button>
                  </>
                )}
              </div>
            </div>
            {whoopSyncing && (
              <div className="whoop-syncing">
                <div className="whoop-spinner" />
                Syncing WHOOP data — this may take a moment...
              </div>
            )}
            {whoopSyncResult && !whoopSyncing && (
              <div className={`whoop-message ${whoopSyncResult.includes('failed') ? 'error' : 'success'}`}>
                {whoopSyncResult}
              </div>
            )}
          </div>
          <div className="profile-whoop-section" style={{ marginTop: '20px' }}>
            <h4>Intervals.icu</h4>
            <p className="whoop-desc">
              Connect Intervals.icu to push workouts from AI Coach directly to your Zwift calendar.
            </p>
            {intervalsMessage && (
              <div className={`whoop-message ${intervalsError ? 'error' : 'success'}`}>
                {intervalsMessage}
                <button className="whoop-message-close" onClick={() => setIntervalsMessage(null)}>&times;</button>
              </div>
            )}
            <div className="oauth-actions-container">
              <div className="oauth-button-group">
                {intervalsConnected ? (
                  <>
                    <div className="status-indicator connected">🔗 Connected</div>
                    <button className="btn btn-danger" onClick={handleIntervalsDisconnect} style={{ fontSize: '0.8rem', padding: '7px 14px' }}>
                      Disconnect
                    </button>
                  </>
                ) : (
                  <>
                    <div className="status-indicator disconnected">❌ Not connected</div>
                    <button className="btn btn-primary" onClick={() => window.location.href = `${API_BASE_URL}/auth/intervals/start`}>
                      Connect Intervals.icu
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Second row: Personal Info (left), Change Password (right) */}
      <div className="profile-second-row">
        <div className="profile-personal-info">
          <h4>{t('profile.personalInfo')}</h4>
          {/* Form groups */}
          <div className="form-group">
            <label>{t('profile.nationality')}:</label>
            <input
              type="text"
              value={profileData.nationality}
              onChange={(e) => setProfileData({...profileData, nationality: e.target.value})}
              placeholder={t('profile.nationality')}
            />
          </div>
          <div className="form-group">
            <label>{t('profile.dateOfBirth')}:</label>
            <input
              type="date"
              value={profileData.date_of_birth}
              onChange={(e) => setProfileData({...profileData, date_of_birth: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>{t('profile.height')}:</label>
            <input
              type="number"
              value={profileData.height_cm}
              onChange={(e) => setProfileData({...profileData, height_cm: e.target.value})}
              placeholder="178"
              step="1"
            />
          </div>
          <div className="form-group">
            <label>{t('profile.weight')}:</label>
            <input
              type="number"
              value={profileData.weight}
              onChange={(e) => setProfileData({...profileData, weight: e.target.value})}
              placeholder="70"
              step="0.1"
            />
          </div>
          <div className="form-group">
            <label>{t('profile.bodyFat')}:</label>
            <input
              type="number"
              value={profileData.body_fat}
              onChange={(e) => setProfileData({...profileData, body_fat: e.target.value})}
              placeholder="15"
              step="0.1"
            />
          </div>
          <div className="form-group">
            <label>{t('profile.gender')}:</label>
            <select
              value={profileData.gender}
              onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
            >
              <option value="">{t('profile.gender')}</option>
              <option value="male">{t('profile.male')}</option>
              <option value="female">{t('profile.female')}</option>
              <option value="other">{t('profile.other')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('profile.bikeType')}:</label>
            <select
              value={profileData.bike_type}
              onChange={(e) => setProfileData({...profileData, bike_type: e.target.value})}
            >
              <option value="">{t('profile.bikeType')}</option>
              <option value="gravel">{t('profile.gravel')}</option>
              <option value="road bike">{t('profile.roadBike')}</option>
              <option value="MTB">{t('profile.mtb')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('profile.language') || 'Language'}:</label>
            <select
              value={currentLanguage}
              onChange={(e) => setLanguage(e.target.value as any)}
            >
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleProfileUpdate}
            disabled={saving}
            className="btn btn-primary oauth-btn"
            style={{ marginBottom: '20px' }}
          >
            {saving ? t('common.loading') : t('profile.saveProfile')}
          </button>

          {/* FTP History */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>FTP History</h3>
            <p style={{ fontSize: '13px', color: '#777', margin: '0 0 8px', lineHeight: '1.5' }}>
              Track your FTP changes over time. The latest entry is used as your current FTP for power zone calculations and analytics.
            </p>
            {currentFtp && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#f0f0f0', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#666' }}>Current FTP:</span>
                <span style={{ fontWeight: 700 }}>{currentFtp} W</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
              <input type="date" value={ftpDate} onChange={e => setFtpDate(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
              <input type="number" value={ftpValue} onChange={e => setFtpValue(e.target.value)}
                placeholder="FTP (W)" min="50" max="600"
                style={{ width: '100px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
              <button onClick={addFtpEntry} disabled={ftpSaving || !ftpValue}
                className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '14px' }}>
                {t('common.add') || 'Add'}
              </button>
            </div>
            {ftpHistory.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {ftpHistory.map(entry => (
                  <div key={entry.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 12px', background: '#f5f5f5', borderRadius: '6px', fontSize: '14px' }}>
                    <span>{entry.date}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: 600 }}>{entry.ftp} W</span>
                      <button onClick={() => deleteFtpEntry(entry.date)}
                        style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#999', fontSize: '14px' }}>No FTP entries yet</p>
            )}
          </div>
        </div>
        <div className="profile-change-password">
          <h4>{t('profile.changePassword')}</h4>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>{t('profile.oldPassword')}:</label>
              <input
                type="password"
                value={oldPassword}
                onChange={e => setOldPassword(e.target.value)}
                autoCapitalize="none"
                required
                style={{ width: '100%', maxWidth: '300px' }}
              />
            </div>
            <div className="form-group">
              <label>{t('profile.newPassword')}:</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoCapitalize="none"
                required
                style={{ width: '100%', maxWidth: '300px' }}
              />
            </div>
            <div className="form-group">
              <label>{t('profile.repeatPassword')}:</label>
              <input
                type="password"
                value={repeatPassword}
                onChange={e => setRepeatPassword(e.target.value)}
                autoCapitalize="none"
                required
                style={{ width: '100%', maxWidth: '300px' }}
              />
            </div>
            {passwordMsg && <div className="success-message">{passwordMsg}</div>}
            {passwordError && <div className="error-message">{passwordError}</div>}
            <button type="submit" className="btn btn-primary oauth-btn" disabled={changingPassword}>
              {changingPassword ? t('common.loading') : t('profile.savePassword')}
            </button>
          </form>
        </div>
      </div>
      {/* Delete Account at the bottom, centered - only for non-admin users */}
      {!user.is_admin && (
      <div className="profile-delete-account">
          <div className="danger-zone-header">
            <h4>{t('profile.dangerZone')}</h4>
          </div>
          <div className="danger-zone-content">
            <button 
              type="button" 
              className="btn btn-danger delete-account-btn" 
              onClick={handleDeleteClick}
            >
              {t('profile.deleteAccount')}
            </button>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-account-modal">
            <div className="modal-header">
              <h4>⚠️ {t('profile.deleteAccount')}</h4>
            </div>
            <div className="modal-body">
              <p className="delete-warning">{t('profile.deleteAccountDescription')}</p>
              
              <form onSubmit={handleDeleteAccount}>
                {user.google_connected ? (
                  <div className="form-group">
                    <label>{t('profile.confirmEmail')}:</label>
                    <input
                      type="email"
                      value={deleteEmail}
                      onChange={e => setDeleteEmail(e.target.value)}
                      required
                      placeholder={t('profile.enterEmail')}
                    />
                  </div>
                ) : (
          <div className="form-group">
            <label>{t('profile.confirmPassword')}:</label>
            <input
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
                      autoCapitalize="none"
              required
                      placeholder={t('profile.enterPassword')}
            />
          </div>
                )}
                
          {deleteError && <div className="error-message">{deleteError}</div>}
                
                <div className="modal-actions">
                  <button 
                    type="submit" 
                    className="btn btn-danger final-delete-btn" 
                    disabled={deleting}
                  >
                    {deleting ? t('common.loading') : t('profile.deleteAccountFinally')}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    {t('common.cancel')}
          </button>
                </div>
        </form>
      </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 